import { useMemo, Fragment } from "react";
import { Users, Calendar as CalendarIcon } from "lucide-react";

/**
 * "Agenda de Terça-feira" — Vista de volume/distribuição.
 *
 * Objectivo (pedido do Coordenador SAC): perceber num relance quais
 * são os dias mais cheios ou vazios para balancear a carga de RTs
 * entre a equipa.
 *
 * Renderiza:
 *   1. Barra horizontal por dia da semana (Seg → Dom) com o número total
 *      de RTs no mês corrente.
 *   2. Heatmap Consultor × dia-da-semana com contagem em cada célula
 *      (mais escuro = mais carga).
 *
 * Todos os cálculos são derivados de ``events`` sem chamar backend.
 */
const DAY_LABELS_PT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

function weekdayOf(iso) {
  // ISO YYYY-MM-DDTHH:MM — no TZ conversion needed for weekday.
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return (d.getDay() + 6) % 7; // 0=Mon … 6=Sun
}

function BarRow({ label, value, max, tone = "primary" }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold w-10">{label}</div>
      <div className="flex-1 h-4 bg-muted/50 rounded-full overflow-hidden border border-border">
        <div
          className={`h-full rounded-full transition-all`}
          style={{
            width: `${pct}%`,
            backgroundColor: tone === "primary" ? "hsl(var(--primary))" : tone,
            minWidth: value > 0 ? "6px" : "0",
          }}
        />
      </div>
      <div className="text-xs font-mono w-8 text-right text-foreground">{value}</div>
    </div>
  );
}

function HeatCell({ count, max, color }) {
  const opacity = max > 0 ? Math.min(1, 0.15 + (count / max) * 0.85) : 0;
  return (
    <div
      className="border border-border rounded-md text-center py-1.5 text-xs font-semibold"
      style={{
        backgroundColor: count > 0 ? color + Math.round(opacity * 255).toString(16).padStart(2, "0") : "transparent",
        color: count > 0 && opacity > 0.5 ? "#fff" : "hsl(var(--muted-foreground))",
      }}
    >
      {count || "·"}
    </div>
  );
}

export default function VolumeView({ events, consultors, monthLabel }) {
  const stats = useMemo(() => {
    // Total per weekday (0=Mon … 6=Sun)
    const byWeekday = [0, 0, 0, 0, 0, 0, 0];
    // consultor_id → weekday → count
    const perConsultor = {};
    // consultor_id → total
    const consultorTotals = {};
    // total for max calc
    let overallMax = 0;
    let noConsultor = 0;

    for (const ev of events || []) {
      const wd = weekdayOf(ev.scheduled_qt_at);
      if (wd == null) continue;
      byWeekday[wd] += 1;
      const cid = ev.consultor_id || "__none__";
      if (!perConsultor[cid]) perConsultor[cid] = [0, 0, 0, 0, 0, 0, 0];
      perConsultor[cid][wd] += 1;
      consultorTotals[cid] = (consultorTotals[cid] || 0) + 1;
      if (!ev.consultor_id) noConsultor += 1;
    }
    overallMax = Math.max(...byWeekday, 1);

    // Order consultors by total desc for the heatmap rows.
    const rows = (consultors || [])
      .filter((c) => perConsultor[c.id])
      .map((c) => ({
        id: c.id,
        name: c.name,
        color: c.color,
        row: perConsultor[c.id],
        total: consultorTotals[c.id],
      }));
    if (perConsultor["__none__"]) {
      rows.push({
        id: "__none__",
        name: "Sem consultor",
        color: "#94A3B8",
        row: perConsultor["__none__"],
        total: consultorTotals["__none__"],
      });
    }
    rows.sort((a, b) => b.total - a.total);

    // Max in heatmap cells (excluding zeroes).
    let heatMax = 1;
    for (const r of rows) for (const v of r.row) if (v > heatMax) heatMax = v;

    // Busy/quiet days.
    const orderedDays = byWeekday
      .map((v, i) => ({ label: DAY_LABELS_PT[i], value: v, idx: i }))
      .sort((a, b) => b.value - a.value);

    return {
      byWeekday, rows, overallMax, heatMax,
      busiest: orderedDays.filter((d) => d.value > 0),
      quietest: orderedDays.slice().reverse().filter((d) => d.value === 0 || d.value < orderedDays[0].value / 2),
      total: events.length,
    };
  }, [events, consultors]);

  return (
    <div className="space-y-6" data-testid="volume-view">
      <div className="bg-card border border-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon className="w-4 h-4 text-primary" />
          <h3 className="font-ui text-base">Volume por dia da semana · {monthLabel}</h3>
          <span className="text-xs text-muted-foreground ml-auto">{stats.total} RT(s) no mês</span>
        </div>
        <div className="space-y-2">
          {DAY_LABELS_PT.map((label, i) => (
            <BarRow
              key={label}
              label={label}
              value={stats.byWeekday[i]}
              max={stats.overallMax}
              tone="primary"
            />
          ))}
        </div>
        {stats.busiest.length > 0 && (
          <div className="mt-4 text-[11px] text-muted-foreground border-t border-border pt-3">
            <div>
              <b>Mais cheio:</b> {stats.busiest[0].label} · {stats.busiest[0].value} RT(s)
              {stats.busiest.length > 1 && (
                <span> · seguido de {stats.busiest[1].label} ({stats.busiest[1].value})</span>
              )}
            </div>
            {stats.quietest.length > 0 && stats.quietest[0].value !== stats.busiest[0].value && (
              <div className="mt-1">
                <b>Mais folgado:</b> {stats.quietest.slice(0, 3).map((d) => `${d.label} (${d.value})`).join(" · ")}
              </div>
            )}
          </div>
        )}
      </div>

      {stats.rows.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-primary" />
            <h3 className="font-ui text-base">Distribuição Consultor × Dia</h3>
            <span className="text-xs text-muted-foreground ml-auto">Célula mais escura = mais carga</span>
          </div>
          <div className="grid grid-cols-[minmax(180px,1fr)_repeat(7,52px)_60px] gap-1.5 text-xs items-center">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Consultor</div>
            {DAY_LABELS_PT.map((label) => (
              <div key={label} className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold text-center">{label}</div>
            ))}
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold text-right">Total</div>

            {stats.rows.map((r) => (
              <Fragment key={r.id}>
                <div className="flex items-center gap-2 text-sm truncate">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
                  <span className="truncate">{r.name}</span>
                </div>
                {r.row.map((v, i) => (
                  <HeatCell key={`${r.id}-${i}`} count={v} max={stats.heatMax} color={r.color} />
                ))}
                <div className="text-right font-mono text-sm">{r.total}</div>
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
